

































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { InputVaultFileSearch, VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t, $tc } from "@/lib/i18nHelpers";
import { UploadVaultFile } from "@/types";
import { EVENTS } from "@/const";
import VaultCategories from "./components/VaultCategories";
import VaultManage from "./components/VaultManage";
import { useVaultCategory } from "./hooks/useVaultCategory";
import { useVaultFiles } from "./hooks/useVaultFiles";
import { SortType } from "./types";

export default defineComponent({
  name: "VaultController",
  components: {
    VaultManage,
    VaultCategories,
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();

    const selectedCategoryId = ref("");
    const filesLoading = ref(false);
    const sortType = ref(SortType.LAST_USE_TIME_DESC);
    const vaultFilesCount = ref(0);

    const {
      vaultCategories,
      categoryScroll,
      vaultCategoriesCount,
      getFirstCategoryPage,
      getNextCategoryPage,
      updateCategory,
      removeCategory,
      updateCategoryPreviewFiles,
      iterativelyFindCategory,
      getCategoryPreview,
    } = useVaultCategory();

    const {
      enableScroll: fileScroll,
      items: vaultFiles,
      getNextPage: getNextFilePage,
      getFirstPage: getFirstFilePage,
      removeItem,
      updateItem,
      addItem,
    } = usePager<VaultFile, InputVaultFileSearch>((page, data) => {
      return providers.VaultsProvider.search({
        data: {
          userId: authUserStrict.value.id,
          categoryIds: [selectedCategoryId.value],
          page,
          sort: sortType.value,
          ...data,
        },
      });
    });

    const { deleteLoading, moveLoading, uploadingFiles, moveFiles, deleteFiles, addFiles } = useVaultFiles(
      selectedCategoryId
    );

    const selectedCategory = computed(() => vaultCategories.value.find(i => i.id === selectedCategoryId.value) ?? null);

    const handleSort = (newSortType: SortType) => {
      sortType.value = newSortType;
    };

    const onAddFiles = async (files: FileList) => {
      handleSort(SortType.LAST_USE_TIME_DESC);
      const uploadVaultFiles = await addFiles(
        files,
        file => {
          updateCategoryPreviewFiles(selectedCategoryId.value, file);
          updateItem(file, f => f.uuid === file.uuid);
        },
        (file, uploadProgress) => {
          updateItem({ ...file, uploadProgress } as VaultFile, f => f.uuid === file.uuid);
        }
      );

      uploadVaultFiles && uploadVaultFiles.forEach(f => addItem(f));
    };

    const onFilesMove = async (
      { files, categoryId }: { files: UploadVaultFile[]; categoryId: string },
      cleanCheckedFiles?: () => void
    ) => {
      const movedFileIds = files.map(f => f.id);
      const response = await moveFiles(movedFileIds, categoryId);
      if (response) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: $t(`{filesCount} ${movedFileIds.length === 1 ? "has" : "have"} been moved`, {
            filesCount: $tc($t("file"), movedFileIds.length),
          }),
        });
        await Promise.all([
          removeItem(i => movedFileIds.includes(i.id)),
          getCategoryPreview([selectedCategoryId.value, categoryId]),
        ]);
        cleanCheckedFiles && cleanCheckedFiles();
      }
    };

    const onFilesDelete = async (files: UploadVaultFile[], cleanCheckedFiles?: () => void) => {
      const deletedFileIds = files.map(f => f.id);
      const response = await deleteFiles(deletedFileIds);
      if (response) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, {
          text: $t(`{filesCount} ${deletedFileIds.length === 1 ? "has" : "have"} been deleted`, {
            filesCount: $tc($t("file"), deletedFileIds.length),
          }),
        });
        await Promise.all([
          removeItem(i => deletedFileIds.includes(i.id)),
          getCategoryPreview([selectedCategoryId.value]),
        ]);
        cleanCheckedFiles && cleanCheckedFiles();
      }
    };

    const onCategoryCreate = async (category: VaultCategory) => {
      selectedCategoryId.value = category.id;
      await iterativelyFindCategory(category.id);
    };

    const onCategoryUpdate = (category: VaultCategory) => {
      selectedCategoryId.value = category.id;
      updateCategory(category);
    };

    const onCategoryDelete = async () => {
      await removeCategory(selectedCategoryId.value);
      handleSort(SortType.LAST_USE_TIME_DESC);
      await getFirstCategoryPage();
      selectedCategoryId.value = "";
    };

    const getFiles = async () => {
      try {
        filesLoading.value = true;
        await getFirstFilePage();
      } finally {
        filesLoading.value = false;
      }
    };

    const getTotalFilesCount = async () => {
      vaultFilesCount.value = (
        await providers.VaultsProvider.search({ data: { userId: authUserStrict.value.id, size: 1 } }, ["id"])
      ).totalRecords;
    };

    watch(
      selectedCategoryId,
      async currentValue => {
        try {
          emit("loading", true);
          const requests = [];
          if (currentValue) {
            requests.push(getFiles());
          } else {
            requests.push(getTotalFilesCount());
            !vaultCategories.value.length && requests.push(getFirstCategoryPage());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    watch([sortType], () => {
      if (!selectedCategoryId.value) return;
      getFiles();
    });

    return {
      moveLoading,
      deleteLoading,
      filesLoading,
      fileScroll,
      categoryScroll,
      selectedCategory,
      selectedCategoryId,
      uploadingFiles,
      vaultCategories,
      vaultCategoriesCount,
      vaultFiles,
      vaultFilesCount,
      sortType,
      getNextCategoryPage,
      getNextFilePage,
      onCategoryCreate,
      onCategoryUpdate,
      onCategoryDelete,
      onFilesDelete,
      onAddFiles,
      onFilesMove,
      handleSort,
    };
  },
});
